// Схема связей в режиме «Иерархия» «Модели ГПР» (владелец 2026-10-06: «хочу, чтобы связи
// задавались на диаграмме Ганта — раздел связи встроен в иерархию и размещается справа, в зоне
// диаграммы Ганта»). Справа от структуры у каждой работы — блок в её строке; его положение по
// горизонтали — уровень в цепочке связей (предшественник левее). Стрелки — связи из шаблонов
// проекта (те же, что на экране «Связи»). Новая связь — потянуть от кружка блока к работе,
// которая идёт следом; щелчок по подписи стрелки — тип, задержка, сдвиг этажей, мягкая, удалить.
// Блоки рисует `Plan.tsx` в ячейках строк (`.sv-node`), стрелки — этот слой поверх таблицы:
// координаты берутся у самих блоков в разметке, как у стрелок Ганта (`SvyaziSloj`).
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { call, p, type Data } from './api';
import { type Anchor } from './Pop';
import { EdgePop, type Edge } from './screens/Links';

type Tpl = Data<'/api/link-templates', 'get'>[number];
type TplRow = Data<'/api/link-templates/{tid}/rows', 'get'>[number];

export const SV_SHAG = 230;      // расстояние между уровнями, px
export const SV_SHIRINA = 196;   // ширина блока
export const SV_OTSTUP = 24;     // слева от первого уровня

/** уровень работы в цепочке: предшественники левее (самый длинный путь от начала) */
export function urovni(edges: { work: string; pred: string }[], names: Set<string>): Map<string, number> {
  const lvl = new Map<string, number>();
  names.forEach(n => lvl.set(n, 0));
  const svoi = edges.filter(e => names.has(e.work) && names.has(e.pred));
  for (let k = 0; k <= names.size; k++) {
    let izm = false;
    svoi.forEach(e => {
      const cand = (lvl.get(e.pred) ?? 0) + 1;
      if (cand > (lvl.get(e.work) ?? 0)) { lvl.set(e.work, cand); izm = true; }
    });
    if (!izm) break;
  }
  return lvl;
}

function podpis(e: Edge): string {
  let t = e.tip === 'НН' ? 'НН' : 'ОН';
  if (e.zapas) t += ' +' + e.zapas + ' дн';
  if (e.operezhenie) t += ' ↑' + e.operezhenie + ' эт';
  if (e.soft) t += ' · мягкая';
  return t;
}

export function SvyaziShema({ pid, box, can, risuj, vybrana, onVybrana, onChanged, onFail }: {
  pid: number; box: React.RefObject<HTMLDivElement>; can: boolean;
  /** меняется — стрелки перемеряются (прокрутка, состав строк) */
  risuj: number;
  vybrana: string | null; onVybrana: (n: string | null) => void;
  onChanged: () => void; onFail: (m: string) => void;
}) {
  const [rows, setRows] = useState<(TplRow & { tpl: string })[]>([]);
  const [tid, setTid] = useState(0);
  const [edge, setEdge] = useState<{ e: Edge; at: Anchor } | null>(null);
  const [temp, setTemp] = useState<{ x1: number; y1: number; x2: number; y2: number } | null>(null);
  const [cel, setCel] = useState<string | null>(null);

  const load = useCallback(async () => {
    const t = await call<Tpl[]>('get', '/api/link-templates?project=' + pid);
    const tpls = t.ok ? t.data : [];
    const vse = await Promise.all(tpls.map(async x => {
      const r = await call<TplRow[]>('get', p('/api/link-templates/{tid}/rows', { tid: x.id }));
      return r.ok ? r.data.map(row => ({ ...row, tpl: x.name })) : [];
    }));
    setRows(vse.flat());
    const glavnyj = tpls.slice().sort((x, y) => y.svyazey - x.svyazey).find(x => x.svyazey > 0)
      || tpls.find(x => x.name.toLowerCase() === 'базовый');
    setTid(glavnyj?.id || 0);
  }, [pid]);
  useEffect(() => { void load(); }, [load]);

  const edges: Edge[] = useMemo(() => rows.map(r => ({ id: r.id, work: r.work, pred: r.pred, tip: r.tip,
    zapas: r.zapas, operezhenie: r.operezhenie, soft: !!r.soft, kom: r.kom || '', tpl: r.tpl })), [rows]);

  const posle = async (r: { ok: boolean; error?: { message: string } }) => {
    if (!r.ok) { onFail(r.error?.message || 'Не сохранилось'); return; }
    await load();
    onChanged();
  };
  async function sozdat(work: string, pred: string) {
    if (work === pred) return;
    if (edges.some(e => e.work === work && e.pred === pred)) { onFail('Такая связь уже есть'); return; }
    let t = tid;
    if (!t) {
      const c = await call<{ id: number }>('post', '/api/link-templates', { name: 'Базовый', project: pid });
      if (!c.ok) { onFail(c.error.message); return; }
      t = c.data.id;
    }
    await posle(await call('post', p('/api/link-templates/{tid}/rows', { tid: t }), { work, pred }) as never);
  }
  async function saveEdge(e: Edge, v: { tip: string; zapas: string; oper: string; soft: boolean; kom: string }) {
    setEdge(null);
    await posle(await call('patch', p('/api/template-rows/{rid}', { rid: e.id as number }),
      { tip: v.tip, zapas: v.zapas || 0, operezhenie_etazhej: v.oper || 0, soft: v.soft, kom: v.kom }) as never);
  }
  async function delEdge(e: Edge) {
    setEdge(null);
    await posle(await call('delete', p('/api/template-rows/{rid}', { rid: e.id as number })) as never);
  }

  // ---- координаты блоков: меряются в разметке таблицы ----
  type Tochka = { x1: number; x2: number; y: number };
  const [koord, setKoord] = useState<Map<string, Tochka>>(new Map());
  const [razmer, setRazmer] = useState({ w: 0, h: 0, ot: 0, verh: 0 });   // ot — правый край закреплённых столбцов, verh — низ шапки
  const perem = useCallback(() => {
    const el = box.current;
    if (!el) return;
    const k = el.getBoundingClientRect();
    const m = new Map<string, Tochka>();
    el.querySelectorAll<HTMLElement>('[data-sv]').forEach(n => {
      const r = n.getBoundingClientRect();
      m.set(n.dataset.sv!, { x1: r.left - k.left + el.scrollLeft, x2: r.right - k.left + el.scrollLeft,
        y: r.top - k.top + el.scrollTop + r.height / 2 });
    });
    setKoord(m);
    // стрелки видны только правее закреплённых столбцов: иначе при прокрутке вбок они ложились
    // на названия (владелец 2026-10-07: «Гант находит на таблицу»)
    const posl = el.querySelector<HTMLElement>('tbody td.ie-rask') || el.querySelector<HTMLElement>('tbody td.fx-w');
    const ot = posl ? posl.getBoundingClientRect().right - k.left + el.scrollLeft : 0;
    // и не выше закреплённой шапки (владелец 2026-10-07: «наезжает на шапку, просвечивает при скроле»)
    const thead = el.querySelector<HTMLElement>('thead');
    const verh = thead ? thead.getBoundingClientRect().bottom - k.top + el.scrollTop : 0;
    setRazmer({ w: el.scrollWidth, h: el.scrollHeight, ot, verh });
  }, [box]);
  useLayoutEffect(() => { perem(); }, [perem, risuj, edges]);
  // горизонтальная прокрутка: закреплённые столбцы остаются на месте, а слой едет вместе с
  // содержимым — край обрезки надо пересчитывать при каждой прокрутке
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    let kadr = 0;
    const f = () => { if (!kadr) kadr = requestAnimationFrame(() => { kadr = 0; perem(); }); };
    el.addEventListener('scroll', f, { passive: true });
    return () => { el.removeEventListener('scroll', f); if (kadr) cancelAnimationFrame(kadr); };
  }, [box, perem]);

  // ---- новая связь мышью: от кружка блока к другой работе ----
  // состояние перетаскивания — в ссылках: при прокрутке схема перемеряет блоки и обработчики
  // переставляются, и начатая связь не должна теряться (владелец 2026-10-07: «тянул связь,
  // проскролил вниз, связь зависла»)
  const otRef = useRef<string | null>(null);
  const koordRef = useRef(koord);
  koordRef.current = koord;
  const sozdatRef = useRef(sozdat);
  sozdatRef.current = sozdat;
  useEffect(() => {
    const el = box.current;
    if (!el || !can) return;
    const xy = (ev: MouseEvent) => {
      const k = el.getBoundingClientRect();
      return { x: ev.clientX - k.left + el.scrollLeft, y: ev.clientY - k.top + el.scrollTop };
    };
    const down = (ev: MouseEvent) => {
      const port = (ev.target as HTMLElement).closest<HTMLElement>('[data-sv-port]');
      if (!port) return;
      ev.preventDefault(); ev.stopPropagation();
      otRef.current = port.dataset.svPort!;
      const a = koordRef.current.get(otRef.current);
      const c = xy(ev);
      setTemp({ x1: a ? a.x2 : c.x, y1: a ? a.y : c.y, x2: c.x, y2: c.y });
    };
    const move = (ev: MouseEvent) => {
      const ot = otRef.current;
      if (!ot) return;
      const c = xy(ev);
      const a = koordRef.current.get(ot);   // начало линии — у блока, даже если его перемерили
      setTemp({ x1: a ? a.x2 : c.x, y1: a ? a.y : c.y, x2: c.x, y2: c.y });
      const nad = (ev.target as HTMLElement).closest<HTMLElement>('[data-sv]');
      setCel(nad && nad.dataset.sv !== ot ? nad.dataset.sv! : null);
    };
    const otmena = () => { otRef.current = null; setTemp(null); setCel(null); };
    const up = (ev: MouseEvent) => {
      const from = otRef.current;
      if (!from) return;
      otmena();
      const nad = (ev.target as HTMLElement).closest<HTMLElement>('[data-sv]');
      if (nad && nad.dataset.sv && nad.dataset.sv !== from) void sozdatRef.current(nad.dataset.sv, from);
    };
    const esc = (ev: KeyboardEvent) => { if (ev.key === 'Escape' && otRef.current) otmena(); };
    el.addEventListener('mousedown', down);
    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', up);
    document.addEventListener('keydown', esc);
    window.addEventListener('blur', otmena);
    return () => {
      el.removeEventListener('mousedown', down); document.removeEventListener('mousemove', move);
      document.removeEventListener('mouseup', up); document.removeEventListener('keydown', esc); window.removeEventListener('blur', otmena);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [box, can]);

  // подсветка цели при перетаскивании
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    el.querySelectorAll<HTMLElement>('[data-sv].sv-cel').forEach(n => n.classList.remove('sv-cel'));
    if (cel) el.querySelector<HTMLElement>('[data-sv="' + CSS.escape(cel) + '"]')?.classList.add('sv-cel');
  }, [cel, box]);

  const linii = useMemo(() => edges.flatMap(e => {
    const a = koord.get(e.pred), b = koord.get(e.work);
    if (!a || !b) return [];
    const x0 = a.x2, y0 = a.y, x1 = b.x1, y1 = b.y;
    const dx = Math.max(30, Math.abs(x1 - x0) / 2);
    const d = x1 >= x0 + 20
      ? `M ${x0} ${y0} C ${x0 + dx} ${y0}, ${x1 - dx} ${y1}, ${x1} ${y1}`
      : `M ${x0} ${y0} C ${x0 + 60} ${y0}, ${x1 - 60} ${y1}, ${x1} ${y1}`;
    const svoya = !!vybrana && (e.work === vybrana || e.pred === vybrana);
    return [{ e, d, mx: (x0 + x1) / 2, my: (y0 + y1) / 2, svoya }];
  }), [edges, koord, vybrana]);

  return (
    <>
      <svg className="viz-svyazi sv-sloj" width={razmer.w} height={razmer.h}>
        <defs>
          <clipPath id="sv-clip"><rect x={razmer.ot} y={razmer.verh} width={Math.max(0, razmer.w - razmer.ot)} height={Math.max(0, razmer.h - razmer.verh)} /></clipPath>
          <marker id="sv-arr" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
            <path d="M 0 0 L 10 5 L 0 10 z" fill="#64748b" />
          </marker>
        </defs>
        <g clipPath="url(#sv-clip)">
        {linii.map(l => {
          const txt = podpis(l.e);
          const w = txt.length * 6.3 + 14;
          return (
            <g key={String(l.e.id)} opacity={vybrana && !l.svoya ? 0.2 : 1}>
              <path d={l.d} fill="none" markerEnd="url(#sv-arr)"
                stroke={l.e.soft ? '#f79009' : l.e.tip === 'НН' ? '#7c3aed' : '#64748b'}
                strokeWidth={l.svoya ? 2.6 : 1.6} strokeDasharray={l.e.soft ? '5 5' : undefined} />
              <g className="sv-lbl" onClick={ev => { ev.stopPropagation(); if (can) setEdge({ e: l.e, at: { x: ev.clientX, y: ev.clientY } }); }}>
                <rect x={l.mx - w / 2} y={l.my - 9} width={w} height={17} rx={7} />
                <text x={l.mx} y={l.my + 3} textAnchor="middle">{txt}</text>
                <title>{can ? 'Щелчок — тип, задержка, сдвиг этажей, мягкая; там же удалить' : txt}</title>
              </g>
            </g>);
        })}
        {temp && <path d={`M ${temp.x1} ${temp.y1} L ${temp.x2} ${temp.y2}`} stroke="#1a56db" strokeWidth={2} fill="none" strokeDasharray="5 4" />}
        </g>
      </svg>
      {edge && <EdgePop at={edge.at} e={edge.e} onClose={() => setEdge(null)}
        onSave={v => void saveEdge(edge.e, v)} onDelete={() => void delEdge(edge.e)} />}
      <span style={{ display: 'none' }} onClick={() => onVybrana(null)} />
    </>
  );
}
